import * as Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/boost';
import '../../highcharts-options';

import * as config from './config.json';
import * as utils from '../../../utils';
import { plotContingencyColumns } from '../contingency-columns'

import { Aeroval } from '../../../types/global';
import { PerformanceData } from '../../../types/data';
import { clearPopup, makePopup } from '../../popup';
declare var aeroval: Aeroval;

// At the top of the file, outside the function
let previewChart: Highcharts.Chart | null = null;

export function plotPerformance(data: PerformanceData | undefined, observation: string, parameterDir: string, layer: string, div: string, _chartOptions: any = {}) {
	if (!data) return;

	var modelSeries = []
	const keyTime = utils.getKeyTime(aeroval.time, aeroval.season);
	const models = Object.keys(data)
	let fairmodeFrequency = ''
	let isDataAvailable = false

	for (const model of models) {
		if (!(data[model] && Object.keys(data[model]).includes(parameterDir))) break
		isDataAvailable = true

		let modelVar = Object.keys(data[model][parameterDir][observation][layer][model])[0]
		let subData = data[model][parameterDir][observation][layer][model][modelVar][keyTime]

		// model color
		const modelStyle = aeroval.modelStyle[model];
		if (modelStyle) {
			var modelColor = modelStyle.color;
		} else {
			const highchartsColor = Highcharts.getOptions().colors as string[];
			if (highchartsColor) {
				modelColor = highchartsColor[0];
			}
		}

		// compute statistics by summing up values at all stations
		let gap = []
		let fa = []
		let ma = []
		for (let station in subData as any) {
			gap.push(subData[station].gap)
			fa.push(subData[station].fa)
			ma.push(subData[station].ma)
			fairmodeFrequency = subData[station].freq
		}

		// success ratio
		let sr = Number(utils.average(gap)) / (Number(utils.average(gap)) + Number(utils.average(fa)))
		//probability of detection
		let pod = Number(utils.average(gap)) / (Number(utils.average(gap)) + Number(utils.average(ma)))

		modelSeries.push({
			zIndex: 99,
			name: model,
			color: modelColor,
			colorAxis: false,
			type: 'scatter',
			cursor: 'pointer',
			data: [{
				x: sr,
				y: pod,
			}],
			marker: {
				radius: 4
			}
		})
	}

	console.log('isDataAvailable', isDataAvailable);
	if (isDataAvailable == false) {
		makePopup('alert', 'No performance plot is available for the current selection', 'warning', false);
	} else {
		clearPopup('alert')
	}

	// add slopes
	const isMobile = (window.screen.width <= 768);
	const slopes = [0.1, 0.2, 0.5, 0.8, 1, 1.2, 1.5, 2.0, 4];
	const guidelineSeries = makeGuideLines(slopes)
	const csi = makeCSI();
	const performanceThreshold = (config as any)[aeroval.parameter.dir]

	const timeString = (aeroval.season == 'All') ? `${aeroval.time}` : `${aeroval.time} (${aeroval.season})`;
	const titleText = `${aeroval.parameter.name} ${fairmodeFrequency} mean performance diagram - ${timeString}`
	const subtitleText = `${observation} - ${fairmodeFrequency} data (thr: ${performanceThreshold} µg.m-3)`
	const xAxisTitle = 'Success Ratio'
	const yAxisTitle = 'Probability of Detection'

	const sideMargins = isMobile ? 75 : 150
	const backgroundOpacity = 0.6


	Highcharts.chart({
		chart: {
			renderTo: div,
			marginLeft: sideMargins,
			marginRight: sideMargins,
			spacingLeft: 0,
			height: 650,
			zooming: {
				type: 'xy'
			}
		},
		title: {
			text: titleText,
		},
		subtitle: {
			text: subtitleText,
		},
		pane: {
			startAngle: 0,
			endAngle: 90,
		},
		xAxis: [
			{
				tickInterval: 0.2,
				min: 0,
				max: 1,
				lineWidth: 1,
				minorGridLineWidth: 1,
				visible: true,
				title: {
					text: xAxisTitle,
				},
				gridLineWidth: 0,
				opposite: false,
				labels: {
					formatter: function () {
						const label = parseFloat(this.axis.defaultLabelFormatter.call(this));
						return String(Math.abs(label));
					},
				},
			},
			{
				linkedTo: 0,
				opposite: true,
			},
		],
		yAxis: [
			{
				min: 0,
				max: 1,
				tickInterval: 0.2,
				minorGridLineWidth: 1,
				title: {
					text: yAxisTitle,
				},
				gridLineWidth: 0,
				lineWidth: 0,
			},
			{
				linkedTo: 0,
				opposite: true,
				gridLineWidth: 0,
				lineWidth: 0,
				title: {
					text: '',
				},
			},
		],
		tooltip: {
			useHTML: true,
			headerFormat: '',
			pointFormat: '<b>{series.name}</b><br>x: {point.x:%.2f}<br>y: {point.y:%.2f}',
		},
		legend: {
			enabled: true,
			layout: 'horizontal',
			align: 'center',
			verticalAlign: 'bottom',
			floating: false,
			y: 10,
			x: 0,
			itemMarginBottom: 5,
			symbolWidth: 12,
			symbolHeight: 12
		},
		colorAxis: {
			min: 0,
			max: 1,
			stops: [
				[0.0, `rgba(215, 48, 39, ${backgroundOpacity})`],
				[0.3, `rgba(252, 141, 89, ${backgroundOpacity})`],
				[0.5, `rgba(254, 224, 139, ${backgroundOpacity})`],
				[0.7, `rgba(217, 239, 139, ${backgroundOpacity})`],
				[1.0, `rgba(26, 152, 80, ${backgroundOpacity})`]
			],
			showInLegend: false
		},
		series: [
			csi,
			...guidelineSeries,
			...modelSeries
		] as any,
		plotOptions: {
			series: {
				events: {
					legendItemClick: function () {
						utils.clickOnLegendItem(this);
					},
				},
				states: {
					inactive: {
						enabled: false,
					},
				},
			},
			scatter: {
				point: {
					events: {
						click: function () {
							if (isMobile) return;

							const modelName = this.series.name;
							const container = document.getElementById('contingency');
							if (!container) return;

							// Show container if hidden
							const wrapper = document.getElementById('contingency');
							if (wrapper) wrapper.style.display = 'block';

							let contingencyBackgroundColor = aeroval.settings.theme == 'light' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'

							// Small delay → gives smoother feeling and lets DOM settle
							setTimeout(() => {
								// Destroy old chart if exists (important!)
								if (previewChart) {
									previewChart.destroy();
									previewChart = null;
								}

								// Create / update small chart
								plotContingencyColumns(
									data[modelName],
									aeroval.observation,
									aeroval.parameter.dir,
									[modelName],
									aeroval.layer,
									'contingency',
									{
										chartHeight: 300,
										chartWidth: 300,
										chartBackgroundColor: contingencyBackgroundColor,
									}
								);

								// Remember the chart instance if plotContingencyColumns returns it
								// If it doesn't return anything → query it manually:
								previewChart = Highcharts.charts.find(c =>
									c && (c as any).renderTo && (c as any).renderTo.id === 'contingency'
								) || null;

							}, 60);
						},
					}
				}
			},
			heatmap: {
				events: {
					click: function () {
						if (isMobile) return;
						// Optional: hide on mouse out
						const wrapper = document.getElementById('contingency');
						if (wrapper) wrapper.style.display = 'none';
					}
				}

			}
		},
	})
}

function makeGuideLines(slopes: number[]): Highcharts.SeriesOptionsType[] {
	return slopes.map((slope) => {
		return {
			type: 'line',
			name: String(slope),
			animation: false,
			dashStyle: 'Dash',
			color: 'gray',
			lineWidth: 1,
			marker: { enabled: false },
			enableMouseTracking: false,
			showInLegend: false,

			// Three points: start, end, label position
			data: [
				{ x: 0, y: 0 },
				{ x: 1, y: slope }
			],
			dataLabels: {
				enabled: true,
				formatter: function (this: Highcharts.Point): string {
					return ((this as any).custom?.label as string | undefined) ?? '';
				},
				align: 'center',
				verticalAlign: 'middle',
				padding: 0,
				y: 0
			},
			tooltip: {
				enabled: false,
			},
		} as Highcharts.SeriesOptionsType
	});
}

function makeCSI() {
	const size = 100;
	const data: number[][] = [];

	for (let i = 0; i < size; i++) {
		const x = (i + 0.5) / size;           // center of bin → nicer visual
		const row: number[] = [];
		for (let j = 0; j < size; j++) {
			const y = (j + 0.5) / size;
			const denom = (1 / x) + (1 / y) - 1;
			const csi = denom > 1e-6 ? 1 / denom : 0;
			row.push(csi);
		}
		data.push(row);
	}
	return {
		type: 'heatmap',
		name: 'CSI',
		showInLegend: false,
		enableMouseTracking: true,
		stickyTracking: false,
		data: data.flatMap((row, y) =>
			row.map((value, x) => [x / 99, y / 99, value])
		),
		colsize: 1 / 99,
		rowsize: 1 / 99,
		opacity: 0.2,
		tooltip: {
			headerFormat: '',
			pointFormat: '<b>x:</b> {point.x:.2f}<br><b>y:</b> {point.y:.2f}<br><b>CSI:</b> {point.value:.3f}'
		}
	};
}
